import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, CircleDashed } from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { Reveal } from "../components/site/Reveal";
import { EnquiryForm } from "../components/site/EnquiryForm";
import { api } from "../lib/api";
import { useTitle } from "../App";

const CATEGORY_MAP = {
  mediaos: "digital-product",
  "six-senses": "general",
  "travel-sense": "travel-experience",
  answerpilot: "digital-product",
  musicverse: "general",
  "drive-pilot": "general",
  "fnb-hospitality": "general",
};

export default function VentureDetail() {
  const { slug } = useParams();
  const [venture, setVenture] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useTitle(venture?.name || "Venture");

  useEffect(() => {
    setVenture(null); setNotFound(false);
    api.get(`/content/ventures/${slug}`).then((r) => setVenture(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-midnight text-mist">
        <Navbar />
        <div className="container-ek flex min-h-[70vh] flex-col items-center justify-center text-center">
          <h1 className="font-display text-4xl font-bold">Venture not found</h1>
          <p className="mt-4 text-slate-400">This venture is not published or does not exist.</p>
          <Link to="/ventures" className="btn-primary mt-8">Back to Ventures</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!venture) return <div className="min-h-screen bg-midnight" />;

  const category = CATEGORY_MAP[venture.slug] || "digital-product";

  return (
    <div data-testid={`venture-page-${venture.slug}`} className="bg-midnight text-mist">
      <Navbar />
      <section className="grain relative overflow-hidden pt-[76px]">
        <div className="container-ek grid items-center gap-12 py-20 sm:py-28 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <Link to="/ventures" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-mist"><ArrowLeft size={14} /> All ventures</Link>
            <p className="eyebrow mb-4">{venture.pillar}</p>
            <h1 className="font-display text-4xl font-bold tracking-tight sm:text-6xl">{venture.name}</h1>
            <p className="mt-3 font-display text-xl text-violet">{venture.tagline}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {venture.ai_tag && <span data-testid="venture-ai-tag" className="rounded-full bg-violet/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-violet">{venture.ai_tag}</span>}
              {venture.status_label && <span data-testid="venture-status" className="rounded-full bg-teal/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-teal">{venture.status_label}</span>}
            </div>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">{venture.summary}</p>
            {venture.availability_note && (
              <p data-testid="venture-availability" className="mt-6 inline-block rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-400">
                {venture.availability_note}
              </p>
            )}
            {venture.product_url && (
              <div className="mt-6">
                <a href={venture.product_url} target="_blank" rel="noopener noreferrer" data-testid="venture-product-link"
                  className="btn-primary">
                  {venture.cta_label || "Visit"} — {venture.product_url.replace(/^https?:\/\//, "")} <ArrowLeft size={15} className="rotate-[135deg]" />
                </a>
              </div>
            )}
          </Reveal>
          {venture.image && (
            <Reveal delay={0.12} className="lg:col-span-6">
              <div className="relative overflow-hidden rounded-[1.6rem] border border-white/[0.08]">
                <img src={venture.image} alt={`${venture.name} — illustrative visual`} className="aspect-[4/3] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 to-transparent" />
                <span className="absolute bottom-4 left-4 rounded-full border border-white/15 bg-midnight/70 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-300 backdrop-blur">Illustrative</span>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section className="bg-cream py-20 text-slatetext sm:py-28">
        <div className="container-ek grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-midnight sm:text-3xl">Purpose</h2>
            <p className="mt-4 text-base leading-relaxed text-slatetext/75">{venture.purpose}</p>
            {venture.audience && (
              <>
                <h2 className="mt-10 font-display text-2xl font-semibold tracking-tight text-midnight sm:text-3xl">Who it is for</h2>
                <p className="mt-4 text-base leading-relaxed text-slatetext/75">{venture.audience}</p>
              </>
            )}
            {venture.benefits?.length > 0 && (
              <>
                <h2 className="mt-10 font-display text-2xl font-semibold tracking-tight text-midnight sm:text-3xl">What it offers</h2>
                <ul className="mt-5 space-y-3">
                  {venture.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-slatetext/80">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-violet" /> {b}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Reveal>
          {venture.capabilities?.length > 0 && (
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="rounded-2xl border border-midnight/[0.08] bg-white p-7">
                <h3 className="font-display text-lg font-semibold text-midnight">Capabilities</h3>
                <p className="mt-1 text-xs text-slatetext/60">Staged honestly: what exists and what is planned.</p>
                <ul className="mt-5 space-y-4">
                  {venture.capabilities.map((c) => (
                    <li key={c.label} className="flex items-start gap-3 text-sm leading-relaxed text-slatetext/80">
                      {c.status === "available"
                        ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-teal" />
                        : <CircleDashed size={16} className="mt-0.5 shrink-0 text-violet" />}
                      <span>
                        {c.label}
                        <span className={`ml-2 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest ${c.status === "available" ? "bg-teal/10 text-teal-hover" : "bg-violet/10 text-violet"}`}>
                          {c.status === "available" ? "Available" : "Planned"}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {venture.faqs?.length > 0 && (
        <section className="py-20 sm:py-28">
          <div className="container-ek max-w-4xl">
            <Reveal>
              <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Questions, answered honestly</h2>
            </Reveal>
            <div className="mt-10 space-y-3">
              {venture.faqs.map((f, i) => (
                <Reveal key={f.q} delay={i * 0.05}>
                  <details data-testid={`faq-${i}`} className="group rounded-xl border border-white/[0.08] bg-deep/60 px-6 py-5 transition-colors open:border-violet/30">
                    <summary className="cursor-pointer list-none font-display text-base font-medium text-mist transition-colors group-open:text-violet">{f.q}</summary>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{f.a}</p>
                  </details>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-deep py-20 sm:py-28">
        <div className="container-ek grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-4">Next Step</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">{venture.cta_label || "Enquire"}</h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400">
              Your enquiry goes straight into our system and is read personally. We reply with substance, not a template.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="card-dark p-7 sm:p-10">
              <EnquiryForm title={`Enquiry — ${venture.name}`} defaultCategory={category} venture={venture.slug} sourcePage={`/ventures/${venture.slug}`} />
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </div>
  );
}
